<template>
  <div class="pb-8">
    <div v-if="loading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="pb-2">
        <el-tooltip effect="dark" placement="right" popper-class="brand-tooltip">
          <div slot="content" class="text-xs">Customer health is still in testing, please let us know if you see anything that looks off.</div>
          <p class="px-4 py-2 text-brand-blue bg-brand-medium-blue bg-opacity-30 text-xs inline-block rounded-lg font-bold mb-1">Beta Feature</p>
        </el-tooltip>
      </div>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div class="space-x-6 flex items-center">
          <p class="inline title">Customer Health</p>
        </div>
        <div>
          <div class="flex items-center space-x-2">
            <el-button v-if="false" type="plain" class="border-brand-gray-blue border-2 px-4 rounded-md">
              <span class="text-xs text-brand-blue font-semibold">Share</span>
            </el-button>
            <!-- <el-button type="plain" class="border-brand-gray-blue border-2 px-4 rounded-md" :disabled="!selectedSegment" @click="exportData">
              <span class="text-xs text-brand-blue font-semibold">Export Data</span>
            </el-button>
            <DatePickerCondensed @change="handleDateChange" :dateRange="dateRange"></DatePickerCondensed>
            <SessionFilter @update="onFilterUpdate" :include="['segmentId']" :preselected="preselectedFilter" /> -->
          </div>
        </div>
      </div>
      <div>
        <!-- <p class="subtitle">
          Detected patterns and trends in customer cancellation reasons. Breakdown by customer segments, MoM changes, real survey response rates,
          follow-up question responses. Sentiment analysis. Trends through the flow. Option to only view responses that are on current blueprint.
        </p> -->
      </div>
    </div>
    <div v-if="loading"></div>
    <div v-else-if="errorLoading">
      <div class="h-52 relative">
        <div class="text-center absolute h-52 flex items-center justify-center inset-0">
          <el-tooltip effect="dark" placement="bottom-start" popper-class="brand-tooltip">
            <div slot="content">
              <span> This should be finished within 24-48 hours.</span>
            </div>
            <p class="px-4 py-2 text-brand-blue bg-brand-medium-blue bg-opacity-30 text-sm inline-block rounded-lg font-bold mb-1">
              <span>We're busy training our models on your data </span>
            </p>
          </el-tooltip>
        </div>
      </div>
    </div>
    <div v-else class="mt-4">
      <div class="grid grid-cols-3">
        <div v-for="group in riskGroups" :key="group.key" class="flex items-center">
          <div class="flex items-center justify-center bg-white rounded-full h-16 w-16" :style="{ backgroundColor: group.bgColor }">
            <span class="text-3xl">{{ group.icon }}</span>
          </div>
          <div class="ml-4 space-y-3">
            <div class="font-bold text-lg text-gray-700">{{ group.label }}</div>
            <div class="flex items-center space-x-3" :style="{ color: group.textColor }">
              <div class="text-4xl font-bold">{{ group.count.toLocaleString() }}</div>
              <div
                class="ml-2 font-bold px-2 py-1 rounded-md space-x-1.5 flex items-center text-sm"
                :style="{ backgroundColor: group.bgColor, color: group.color }"
              >
                <font-awesome-icon :icon="['fas', group.countPctChange > 0 ? 'arrow-up' : 'arrow-down']" />
                <span>{{ group.countPctChange | formatPercent }}</span>
              </div>
            </div>
            <div class="text-lg font-semibold text-brand-brown">{{ (group.count / totalCount) | formatPercent }} of customers</div>
          </div>
        </div>
      </div>
      <div class="mt-12">
        <hr class="w-full border-brand-sand" />
      </div>
      <div class="mt-10">
        <div class="text-lg text-gray-800 font-semibold">Revenue Churn Risk</div>
        <div v-if="riskGroups.length" class="mt-8">
          <PieBarWrapper
            :series="riskGroups"
            :barHeight="20"
            :labelFontSize="15"
            :valueAccessor="(d) => d.mrr"
            :valueFormatter="(d) => `${formatCondensedMoney(d)} MRR`"
          />
        </div>
      </div>
      <div class="mt-12">
        <hr class="w-full border-brand-sand" />
      </div>
      <div class="mt-10">
        <SentimentTrend />
      </div>
    </div>
  </div>
</template>

<script>
import StatService from '@/api/stats.service';
import PieBarWrapper from '@/components/Vis/D3/Charts/PieBarWrapper.vue';
import { sum } from 'd3-array';
import { currencyFormat } from '@/helpers/stats-utils';
import { formatCondensedMoney } from '@/helpers/jsUtils';
import { mapGetters } from 'vuex';
import SentimentTrend from './SentimentTrend.vue';

const groups = [
  { label: 'Low Risk', key: 'low', color: '#52A475', icon: '🏝️', bgColor: '#D9F9E6', textColor: '#163523' },
  { label: 'Medium Risk', key: 'medium', color: '#B06FD3', icon: '🚸', bgColor: '#F2E7F8', textColor: '#431646' },
  { label: 'High Risk', key: 'high', color: '#EE7963', icon: '🏜️', bgColor: '#FAE7E2', textColor: '#461A11' },
];

export default {
  name: 'HealthOverview',
  components: {
    PieBarWrapper,
    SentimentTrend,
  },
  data() {
    return {
      loading: false,
      health: null,
      previousHealth: null,
      errorLoading: false,
      currencyFormat,
      formatCondensedMoney,
    };
  },
  async created() {
    try {
      const res = await StatService.getCompanyHealth();
      this.health = res.latestHealth;
      this.previousHealth = res.previousHealth || res.latestHealth;
    } catch (e) {
      console.log(e);
      this.$message({
        message: 'Error loading customer health dashboard',
        type: 'error',
      });
      this.errorLoading = true;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    ...mapGetters(['confidential']),
    totalCount() {
      return sum(this.riskGroups, (d) => d.count);
    },
    riskGroups() {
      if (!this.health || !this.previousHealth) {
        return [];
      }
      return groups.map((group) => {
        const currentMetric = this.health.metrics[group.key];
        const previousMetric = this.previousHealth.metrics[group.key];
        const countChange = currentMetric.count - (previousMetric ? previousMetric.count : 0);
        const mrrChange = currentMetric.mrr - (previousMetric ? previousMetric.mrr : 0);
        const countPctChange = previousMetric && previousMetric.count !== 0 ? countChange / previousMetric.count : 0;
        const mrrPctChange = previousMetric && previousMetric.mrr !== 0 ? mrrChange / previousMetric.mrr : 0;
        return {
          ...group,
          count: currentMetric.count,
          mrr: currentMetric.mrr,
          countChange,
          mrrChange,
          countPctChange,
          mrrPctChange,
        };
      });
    },
  },
};
</script>
